import { EnumMedicineType } from 'src/constants/enum';
import { IPaymentAccount } from './PaymentEntity';
import { BookkeepingPolicyDict } from './SettingEntity';

/**
 * 配药中心明细接口
 * @export
 * @interface IDrugStoreInfo
 */
export interface IDrugStoreInfo {
  id: number;
  uuid: string;
  agent: number;
  name: string;
  location: number;
  address: string;
  is_active: boolean;
  is_online: boolean;
  currency: string;
  admin_username: string;
  admin_name: string;
  admin_email?: string;
  admin_mobile: string;
  /**
   * 用户user表的uuid
   */
  admin_uuid?: string;
  created_time: string;
  modified_time: string;
  /**
   * 支付账号信息
   */
  payment_accounts: IPaymentAccount[];
  /**
   * 默认支付账号
   */
  default_payment: string;
  /**
   * 是否允许医生记账
   */
  bookkeeping_policy?: BookkeepingPolicyDict;
  /**
   * true: 国内账号，false：海外账号
   */
  is_domestic: boolean;
  /**
   * 是否允许修改药材配置
   */
  allow_change_medicine_setting: boolean;

  package_price?: number;
  std_freight_price?: number;
  /** 配药中心区域范围 */
  regions?: number[];
  /** 配药中心公告 */
  notice?: string;
  /** 配药中心是否开启草药待煎服务 */
  decoction_enabled?: boolean;
  /** 草药待煎服务是否必选 */
  decoction_required?: boolean;
  /** 待煎费用（原：草药待煎服务每副价格） */
  decoction_price?: number;
  /** 待煎费用 单位 */
  decoction_unit?: number;
  /** 代煎容量 */
  decoction_capacity?: number;
  /** 代煎配送范围 */
  decoction_regions?: number[];
  /** 卖家UUID */
  seller_user: string;
  /** 运费模板 */
  freight_templates: IFreightTemplate[];
  /** 运费分润方: [脉景 | 配药中心] */
  express_profit_recipient: 'macrocura' | 'seller';
  /** 快递公司code */
  express_company: string | null;
  /** 业务类型 */
  express_type: number | null;
  /** 面单模板 */
  express_bill_template: number | null;
  /** 运费支付方: [脉景 | 配药中心] */
  express_payer: 'macrocura' | 'seller';
  /** 结算方式: [现付 | 月结] */
  express_payment_type: 'PAYG' | 'MON';
  /** 月结账号 */
  express_payment_account: string | null;
  /** 发货人 */
  delivery_name: string | null;
  /** 手机号 */
  delivery_mobile: string | null;
  /** 发货地址(区县id) */
  delivery_location: string | null;
  /** 发货详细地址 */
  delivery_address: string | null;
  /**  是否开启加工服务 */
  processing_enabled: boolean;
  /** 必选代加工服务 */
  processing_required: boolean;
  /** 加工服务模板 */
  processing_templates: IProcessingTemplates[];
  /** 上线时是否需要检查资管账号开通(默认平台支持) */
  used_asset_account: boolean;
  /** 医保方式: 是否使用线上医保, 默认不支持 */
  used_online_medical_insurance: boolean;
  /** 医保方式: 是否使用线下医保, 默认不支持 */
  used_offline_medical_insurance: boolean;
  /** 取药方式: 是否快递, 默认支持 */
  used_express: boolean;
  /** 取药方式: 是否自取, 默认不支持 */
  used_self_mention: boolean;
  /** 自取地址区编码 */
  self_mention_location: string | null;
  /** 自提详细地址 */
  self_mention_address: string | null;
}

/**
 * 加工方式
 */
export enum ProcessMethod {
  /** 草药代煎 */
  HerbalDecoction = 'decoction',
  /** 制粉剂 */
  TinPowder = 'tiny_powder',
  /** 制散剂 */
  Powder = 'powder',
  /** 制膏 */
  Paste = 'paste',
  /** 制浓缩膏 */
  CondensePaste = 'condense_paste',
  /** 制蜜丸 */
  Pill = 'pill',
  /** 制大蜜丸 */
  BigPill = 'big_pill',
  /** 制小蜜丸 */
  SmallPill = 'small_pill',
  /** 制水丸 */
  WaterPill = 'water_pill',
  /** 制浓缩丸 */
  CondensePill = 'condense_pill',
  /** 制胶囊 */
  Capsule = 'capsule',
  /** 共煎制粒 */
  DecoctionAndGrain = 'decoction_and_grain',
  /** 制酒剂 */
  Alcohol = 'alcohol',
  /** 颗粒剂制膏 */
  GrainAndPaste ='grain_and_paste',
  /** 浓煎剂 */
  CondenseDecoction = 'condense_decoction',
  /** 自煎 */
  SelfDecoction = 'self_decoction',
}

/**
 * 中药代加工服务模板数据结构
 *
 * @exports
 * @interface IProcessingTemplates
 */
export interface IProcessingTemplates {
  /** 加工方法 */
  method: ProcessMethod;
  /** 价格单位 */
  price_unit: DecoctionUnitDict;
  /** 单价 */
  price: number;
  /** 是否为默认 */
  is_default?: boolean;
  /** 适用剂型 */
  applicative_dosage_forms: EnumMedicineType[];
  /** 包装费价格单位 */
  package_price_unit: DecoctionUnitDict;
  /** 包装费 */
  package_price: number;
}

/**
 * 加工费用单位字典
 */
export enum DecoctionUnitDict {
  /** 每服 */
  Times = 0,
  /** 每剂 */
  Dose = 1,
  /** 每处方 */
  Prescription = 2,
  /** 每单 */
  Order = 3,
  /** 每公斤 */
  Kilo = 4,
  /** 每百克 */
  HundredGram = 5,
}

/**
 * 创建/更新 配药中心接口
 * 配药中心接口子集
 * @export
 * @interface ISubDrugStore
 */
export interface ISubDrugStore {
  name?: string;
  address?: string;
  currency?: string;
  default_payment?: string;
  payment_accounts?: IPaymentAccount[];
  admin_mobile?: string;
  location?: number;
  admin_username?: string;
  is_active?: boolean;
  /**
   * 是否允许医生记账(已废弃)
   */
  bookkeeping_policy?: BookkeepingPolicyDict;
  /** 配药中心收取用户的快递费 */
  std_freight_price?: number;
  /** 配药中心包邮价格 */
  package_price?: number;
  admin_name?: string;
  admin_password?: string;
  admin_email?: string;
  /** 配药中心公告 */
  notice?: string;
  /** 配药中心是否开启草药待煎服务 */
  decoction_enabled?: boolean;
  /** 草药待煎服务是否必选 */
  decoction_required?: boolean;
  /** 草药待煎服务每副价格 */
  decoction_price?: number;
  /** 待煎费用 单位  */
  decoction_unit?: number;
  /** 代煎容量 */
  decoction_capacity?: number;
  /** 代煎配送范围 */
  decoction_regions?: number[];
}

/**
 * 配药中心状态接口
 * @export
 * @interface IDrugStoreStatus
 */
export interface IDrugStoreStatus {
  value: boolean;
  name: string;
  key: string;
}

/**
 * 运费模板
 *
 * @export
 * @interface IFreightTemplate
 */
export interface IFreightTemplate {
  uuid: string;
  freight_template_content: Array<{
    /** 地区 */
    regions: number[];
    /** 包邮金额/件数等 */
    free_amount?: number;
    /** 是否包邮枚举 */
    free_unit: EnumFreightFreeCondition;
    /** 首重 */
    initial_weight: number;
    /** 首重金额 */
    initial_price: number;
    /** 续重 */
    extra_weight: number;
    /** 续重金额 */
    extra_price: number;
  }>;
  template_name: string;
  is_default: boolean;
  medicine_group_infos: [
    {
      uuid: string;
      name: string;
    }
  ];
}

/**
 * 包邮条件类型枚举
 *
 * @export
 * @enum {number}
 */
export enum EnumFreightFreeCondition {
  '不包邮' = 0,
  '满XX元包邮' = 1,
  '满XX件包邮' = 2,
}



// WEBPACK FOOTER //
// ./src/entity/DrugStoreEntity.ts